import { useCallback, useEffect, useState } from "react";
import { fetchRfm, fetchRfmSegment, fetchRfmTemplate } from "@/api/rfm/api";
import type { RfmItem, RfmOrderBy, RfmOrderByDirection, RfmSegmentCompaniesItem, RfmTemplateItem } from "@/types/rfm";
import { RfmAnalysisChart } from "./RfmAnalysisChart";
import { RfmSegmentBreakdown } from "./RfmSegmentBreakdown";
import { SelectedSegmentCustomers } from "./SelectedSegmentCustomers";
import { Spinner } from "@/components/common/Loading/Spinner";
import { ErrorDialog } from "@/components/common/ErrorDialog/ErrorDialog";

const PAGE_SIZE = 10;
const ERROR_MESSAGE = "We couldn't load the RFM analysis. Please try again.";

export const RfmWidget = () => {
  const [items, setItems] = useState<RfmItem[]>([]);
  const [template, setTemplate] = useState<RfmTemplateItem[]>([]);
  const [selectedSegmentId, setSelectedSegmentId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customers, setCustomers] = useState<RfmSegmentCompaniesItem[]>([]);
  const [totalRows, setTotalRows] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [orderBy, setOrderBy] = useState<RfmOrderBy | null>(null);
  const [orderByDirection, setOrderByDirection] = useState<RfmOrderByDirection | null>(null);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [customersReloadKey, setCustomersReloadKey] = useState(0);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);

  useEffect(() => {
    if (selectedSegmentId === null) {
      setCustomers([]);
      setTotalRows(null);
      setCustomersError(null);
      return;
    }

    let active = true;
    setCustomersLoading(true);
    setCustomersError(null);
    fetchRfmSegment(selectedSegmentId, pageNumber, PAGE_SIZE, orderBy ?? undefined, orderByDirection ?? undefined)
      .then(response => { if (active) { setCustomers(response.items ?? []); setTotalRows(response.totalRows ?? null); } })
      .catch(requestError => {
        if (!active) return;
        setCustomers([]);
        setTotalRows(null);
        console.error(requestError);
        setCustomersError("Unable to load segment customers.");
        setErrorDialogOpen(true);
      })
      .finally(() => { if (active) setCustomersLoading(false); });
    return () => { active = false; };
  }, [selectedSegmentId, pageNumber, orderBy, orderByDirection, customersReloadKey]);

  const handleSelectSegment = (segmentId: number) => {
    if (segmentId === selectedSegmentId && customersError) {
      setCustomersError(null);
      setCustomersReloadKey(key => key + 1);
    }
    setSelectedSegmentId(segmentId);
    setPageNumber(1);
    setOrderBy(null);
    setOrderByDirection(null);
  };

  const handleSortChange = (column: RfmOrderBy) => {
    setOrderByDirection(orderBy === column && orderByDirection === "Asc" ? "Desc" : orderByDirection === null || orderBy === column ? "Asc" : orderByDirection);
    setOrderBy(column);
    setPageNumber(1);
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([fetchRfm(), fetchRfmTemplate()])
      .then(([response, templateResponse]) => {
        if (!active) return;
        setItems(response.items ?? []);
        setTemplate(templateResponse.items ?? []);
        setError(null);
      })
      .catch(requestError => {
        if (!active) return;
        console.error(requestError);
        setError("Unable to load RFM analysis.");
        setErrorDialogOpen(true);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [reloadKey]);

  const handleRetry = useCallback(() => {
    setErrorDialogOpen(false);
    if (error) {
      setError(null);
      setReloadKey(key => key + 1);
    } else if (customersError) {
      setCustomersError(null);
      setCustomersReloadKey(key => key + 1);
    }
  }, [error, customersError]);

  const handleCancel = useCallback(() => setErrorDialogOpen(false), []);

  const errorDialog = (
    <ErrorDialog open={errorDialogOpen} message={ERROR_MESSAGE} onRetry={handleRetry} onCancel={handleCancel} />
  );

    if (loading) return (
        <div className="p-6 flex flex-col items-center gap-2">
            <Spinner size="lg" />
            <span className="text-slate-600">Loading RFM analysis...</span>
        </div>
    );
  if (error || !items.length || !template.length) return (
    <>
      <div className="p-6 text-sm text-slate-600">No RFM data is available for this account.</div>
      {errorDialog}
    </>
  );

  const selectedSegment = template.find(segment => segment.segmentId === selectedSegmentId);

  return (
    <div className="flex flex-col gap-6 pt-6">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:items-stretch">
        <RfmAnalysisChart items={items} template={template} selectedSegmentId={selectedSegmentId} onSelectSegment={handleSelectSegment} />
        <RfmSegmentBreakdown items={items} template={template} selectedSegmentId={selectedSegmentId} onSelectSegment={handleSelectSegment} />
      </div>
      {selectedSegmentId !== null && (
        customersError ? <div className="p-6 text-sm text-slate-600">Customers for this segment couldn't be loaded. Select the segment again to retry.</div>
                  : customersLoading ?
                      <div className="p-6 flex flex-col items-center gap-2">
                        <Spinner size="lg" />
                          <span className="text-slate-600">Loading customers...</span>
                    </div>
        : <SelectedSegmentCustomers
            title={`${selectedSegment?.segmentName ?? "Selected segment"} customers`}
            segmentName={selectedSegment?.segmentName}
            description={selectedSegment?.description ?? `${customers.length} customers in this segment.`}
            customers={customers}
            totalCount={totalRows ?? customers.length}
            pageNumber={pageNumber}
            pageSize={PAGE_SIZE}
            orderBy={orderBy}
            orderByDirection={orderByDirection}
            onPageChange={setPageNumber}
            onSortChange={handleSortChange}
          />
      )}
      {errorDialog}
    </div>
  );
};